<script setup lang="ts">
import type { EventIdeaResource } from '@@/types/api';
import { useCardInterest } from '~/composables/use-card-interest';

const props = defineProps<{
  event: EventIdeaResource;
}>();

const placeholder = useEventPlaceholderImage();

// Per-card interest wiring (idea source). An idea is a NON-transactional,
// interest-gauging placeholder — the only interactive control it ever gets is
// the "Interested" toggle. Ideas are Interested-only: there is no "Going" rung
// (nothing concrete to be going to yet), so the going seed is a constant zero
// and `isConcluded` is never set (ideas carry coarse timing and never conclude).
// No pricing / commit / roster is introduced here.
const {
  descriptor: interestDescriptor,
  interestedCount,
  goingCount,
  isPending: isInterestPending,
  snackbar: interestSnackbar,
  snackbarText: interestSnackbarText,
  snackbarColor: interestSnackbarColor,
  setInterest,
} = useCardInterest({
  source: 'idea',
  eventId: computed(() => props.event.id),
  initialInterested: props.event.interested_count,
  initialGoing: 0,
  title: props.event.title,
});
</script>

<template>
  <v-card
    :to="`/ideas/${event.id}`"
    variant="outlined"
    rounded="lg"
    class="idea-card"
  >
    <v-img
      :src="event.cover_image_url ?? placeholder"
      :alt="event.title"
      :aspect-ratio="16 / 9"
      cover
      class="bg-surface-variant"
    >
      <!-- A distinct "gauging interest" badge so an idea card never reads as a
           confirmed, commitment-grade event. This is NOT the third-party badge
           (that marker is reserved for Almanac-mirrored informational cards). -->
      <div class="d-flex justify-end pa-2">
        <v-chip
          color="secondary"
          variant="elevated"
          size="small"
          prepend-icon="mdi-lightbulb-on-outline"
        >
          Coming soon
        </v-chip>
      </div>
    </v-img>

    <v-card-title class="text-subtitle-1 font-weight-bold pb-1">
      {{ event.title }}
    </v-card-title>

    <v-card-subtitle class="pb-2">
      <v-icon size="small" class="mr-1">mdi-calendar</v-icon>
      <!-- Rendered verbatim from the backend timing tagged union — never
           re-format or derive a datetime on the frontend. -->
      {{ event.timing.display }}
      <br />
      <v-icon size="small" class="mr-1">mdi-map-marker</v-icon>
      {{ event.location_hint.display }}
    </v-card-subtitle>

    <v-card-text v-if="event.author?.display_name" class="pt-0">
      <div class="text-body-2 text-medium-emphasis">
        Floated by {{ event.author.display_name }}
      </div>
    </v-card-text>

    <v-card-text class="pt-0">
      <!-- Social proof: always shown, reflects optimistic interest toggles.
           Ideas have no "going" bucket, so the going count is always zero. -->
      <EventsSocialProofCounts
        :interested-count="interestedCount"
        :going-count="goingCount"
        class="mb-2"
      />

      <!-- Interest toggle. Wrapped in a click guard so tapping the control never
           triggers the card's :to navigation to the detail page. -->
      <div
        v-if="interestDescriptor.visible"
        class="idea-card__interest"
        @click.stop.prevent
      >
        <EventsEventInterestControl
          :descriptor="interestDescriptor"
          :event-id="event.id"
          :pending="isInterestPending"
          @select="setInterest"
        />
      </div>
    </v-card-text>
  </v-card>

  <v-snackbar
    v-model="interestSnackbar"
    :color="interestSnackbarColor"
    :timeout="4000"
    location="bottom"
  >
    {{ interestSnackbarText }}
  </v-snackbar>
</template>

<style scoped>
.idea-card {
  transition: box-shadow 0.2s ease;
  text-decoration: none !important;
}
.idea-card:hover {
  box-shadow: 0 4px 12px rgb(0 0 0 / 12%);
}
</style>
